<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxForm, DxSimpleItem, DxRequiredRule } from 'devextreme-vue/form';
import { bottoGridPaging, showError, showSuccess } from '@botto/core';
import {
  domainGroupsApi,
  domainsApi,
  trackerNodesApi,
  type TrackerDomain,
  type TrackerGroup,
  type TrackerNode,
} from '../../api';
import StatusBadge from '../../components/StatusBadge.vue';
import TrackerListLayout from '../../components/TrackerListLayout.vue';
import { formatDateTime } from '../../utils/format';
import { pageFromLoadOptions, resetGridPage, trackerGridProps } from '../../utils/gridUi';

const { paging, pager } = bottoGridPaging({ pageSize: 50 });
const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const groups = ref<TrackerGroup[]>([]);
const nodes = ref<TrackerNode[]>([]);
const activeNodes = ref<TrackerNode[]>([]);
const groupFilter = ref<number | null>(null);

const showModal = ref(false);
const editing = ref<TrackerDomain | null>(null);
const saving = ref(false);
const formError = ref('');
const form = reactive({
  name: '',
  status: 'active',
  landing_path: '/',
  group_id: null as number | null,
  tracker_node_id: null as number | null,
});

const statusItems = ['active', 'paused', 'disabled'];

function defaultGroupId(): number | null {
  const def = groups.value.find((g) => g.name === 'Default');
  return def?.id ?? groups.value[0]?.id ?? null;
}

function defaultNodeId(): number | null {
  const def = nodes.value.find((n) => n.name === 'Default' && n.status === 'active');
  if (def) return def.id;
  return activeNodes.value[0]?.id ?? null;
}

async function loadGroups() {
  const res = await domainGroupsApi.listDomainGroups({ page: 1, page_size: 200 });
  groups.value = res.items || [];
}

async function loadNodes() {
  const res = await trackerNodesApi.listTrackerNodes({ page: 1, page_size: 200 });
  nodes.value = res.items || [];
  activeNodes.value = nodes.value.filter((n) => n.status === 'active');
}

/** Active nodes plus currently assigned (may be paused) so the select stays valid. */
const nodeSelectItems = computed(() => {
  const byId = new Map(activeNodes.value.map((n) => [n.id, n]));
  const currentId = form.tracker_node_id;
  if (currentId != null && !byId.has(currentId)) {
    const cur = nodes.value.find((n) => n.id === currentId);
    if (cur) byId.set(cur.id, cur);
  }
  return [...byId.values()];
});

const store = new CustomStore({
  key: 'id',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const { page, page_size } = pageFromLoadOptions(loadOptions, paging.pageSize);
    const gid = groupFilter.value;
    const params: { page: number; page_size: number; group_id?: number } = { page, page_size };
    if (gid != null && Number.isFinite(gid)) params.group_id = Number(gid);
    try {
      const res = await domainsApi.listDomains(params);
      return { data: res.items || [], totalCount: res.total || 0 };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

function refreshGrid() {
  resetGridPage(grid.value?.instance ?? null);
}

function onGroupFilter(e: { value?: number | null }) {
  const next = e.value ?? null;
  if (next === groupFilter.value) return;
  groupFilter.value = next;
  refreshGrid();
}

async function refresh() {
  try {
    await grid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

function openCreate() {
  editing.value = null;
  form.name = '';
  form.status = 'active';
  form.landing_path = '/';
  form.group_id = defaultGroupId();
  form.tracker_node_id = defaultNodeId();
  formError.value = '';
  showModal.value = true;
}

function openEdit(d: TrackerDomain) {
  editing.value = d;
  form.name = d.name;
  form.status = d.status;
  form.landing_path = d.landing_path;
  form.group_id = d.group_id ?? defaultGroupId();
  form.tracker_node_id = d.tracker_node_id ?? defaultNodeId();
  formError.value = '';
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function save() {
  saving.value = true;
  formError.value = '';
  try {
    if (editing.value) {
      const body: {
        status: string;
        landing_path: string;
        group_id?: number;
        tracker_node_id?: number;
      } = {
        status: form.status,
        landing_path: form.landing_path,
      };
      if (form.group_id) body.group_id = form.group_id;
      if (form.tracker_node_id) body.tracker_node_id = form.tracker_node_id;
      await domainsApi.updateDomain(editing.value.id, body);
      showSuccess('Домен обновлён');
    } else {
      if (!form.name.trim()) {
        formError.value = 'Укажите имя (hostname)';
        return;
      }
      const body: {
        name: string;
        status: string;
        landing_path: string;
        group_id?: number;
        tracker_node_id?: number;
      } = {
        name: form.name.trim(),
        status: form.status,
        landing_path: form.landing_path || '/',
      };
      if (form.group_id) body.group_id = form.group_id;
      if (form.tracker_node_id) body.tracker_node_id = form.tracker_node_id;
      await domainsApi.createDomain(body);
      showSuccess('Домен создан');
    }
    closeModal();
    refreshGrid();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : 'Ошибка сохранения';
  } finally {
    saving.value = false;
  }
}

const checkingAll = ref(false);
const checkingId = ref<number | null>(null);

function dnsLabel(d: TrackerDomain): string {
  const st = d.dns_status || 'unknown';
  const reason = d.dns_reason || '';
  if (st === 'active') {
    if (reason === 'cloudflare') return 'OK (Cloudflare)';
    if (reason === 'direct') return 'OK (A→server)';
    if (reason === 'local') return 'OK (local)';
    return 'OK';
  }
  if (st === 'pending') {
    if (reason === 'no_resolve') return 'Нет A-записи';
    if (reason.startsWith('wrong_ip:')) return `Чужой IP (${reason.slice(9)})`;
    return reason || 'pending';
  }
  if (reason === 'server_ip_unknown') return 'IP ноды неизвестен';
  return 'не проверен';
}

function nodeLabel(d: TrackerDomain): string {
  const name = d.tracker_node_name || '—';
  const ip = d.tracker_node_ip || '';
  return ip ? `${name} (${ip})` : name;
}

async function checkOneDns(d: TrackerDomain) {
  checkingId.value = d.id;
  try {
    await domainsApi.checkDomainDns(d.id);
    showSuccess(`DNS: ${d.name}`);
    await refresh();
  } catch (e) {
    showError(e);
  } finally {
    checkingId.value = null;
  }
}

async function checkAllDns() {
  checkingAll.value = true;
  try {
    const res = await domainsApi.checkAllDomainsDns();
    const ip = res.server_ip || '—';
    showSuccess(`DNS проверен: ${res.checked} дом. (server ${ip})`);
    await refresh();
  } catch (e) {
    showError(e);
  } finally {
    checkingAll.value = false;
  }
}

onMounted(async () => {
  try {
    await Promise.all([loadGroups(), loadNodes()]);
  } catch (e) {
    showError(e);
  }
});
</script>

<template>
  <TrackerListLayout title="Домены">
    <template #actions>
      <DxButton
        icon="globe"
        text="Проверить DNS"
        styling-mode="outlined"
        :disabled="checkingAll"
        @click="checkAllDns"
      />
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <template #hint>
      Landing-файлы лежат в <code>landings/&lt;domain&gt;/</code>. DNS: A-запись должна указывать на
      <strong>public IP выбранной edge-ноды</strong> (или Cloudflare).
    </template>

    <template #toolbar>
      <DxSelectBox
        :value="groupFilter"
        :items="groups"
        display-expr="name"
        value-expr="id"
        :width="240"
        placeholder="Все группы"
        :show-clear-button="true"
        @value-changed="onGroupFilter"
      />
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="store"
      :remote-operations="{ paging: true, sorting: false, filtering: false }"
      v-bind="trackerGridProps"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
      @row-dbl-click="(e: any) => openEdit(e.data)"
    >
      <DxScrolling mode="standard" />

      <DxColumn data-field="id" caption="ID" width="80" />
      <DxColumn data-field="name" caption="Имя" css-class="mono" />
      <DxColumn
        data-field="group_name"
        caption="Группа"
        :calculate-cell-value="(r: TrackerDomain) => r.group_name || '—'"
      />
      <DxColumn
        data-field="tracker_node_name"
        caption="Edge"
        width="200"
        css-class="mono"
        :calculate-cell-value="(r: TrackerDomain) => nodeLabel(r)"
      />
      <DxColumn data-field="status" caption="Статус" width="120" cell-template="statusCell" />
      <DxColumn
        data-field="dns_status"
        caption="DNS"
        width="180"
        cell-template="dnsCell"
      />
      <DxColumn data-field="landing_path" caption="Landing path" css-class="mono" />
      <DxColumn
        data-field="updated_at"
        caption="Обновлён"
        width="180"
        :calculate-cell-value="(row: TrackerDomain) => formatDateTime(row.updated_at)"
      />

      <DxColumn type="buttons" :width="140" caption="Действия">
        <DxGridButton hint="Изменить" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
        <DxGridButton
          hint="Проверить DNS"
          icon="globe"
          :on-click="(e: any) => checkOneDns(e.row.data)"
        />
      </DxColumn>

      <template #statusCell="{ data }">
        <StatusBadge :status="data.data.status" />
      </template>
      <template #dnsCell="{ data }">
        <span
          class="dns-badge"
          :class="{
            'dns-badge--ok': data.data.dns_status === 'active',
            'dns-badge--bad': data.data.dns_status === 'pending',
            'dns-badge--unk': !data.data.dns_status || data.data.dns_status === 'unknown',
          }"
          :title="[data.data.dns_ip, data.data.dns_reason, data.data.dns_checked_at].filter(Boolean).join(' · ')"
        >
          {{ dnsLabel(data.data) }}
        </span>
      </template>
    </DxDataGrid>

    <template #overlays>
      <DxPopup
        v-model:visible="showModal"
        :title="editing ? 'Редактировать домен' : 'Добавить домен'"
        :width="480"
        :height="'auto'"
        :show-close-button="true"
        @hiding="closeModal"
      >
        <template #content>
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <DxForm :form-data="form" label-location="top" :col-count="1">
            <DxSimpleItem
              v-if="!editing"
              data-field="name"
              editor-type="dxTextBox"
              :label="{ text: 'Имя (hostname)' }"
            >
              <DxRequiredRule message="Обязательно" />
            </DxSimpleItem>
            <DxSimpleItem
              v-else
              :label="{ text: 'Имя' }"
              template="nameReadonly"
            />
            <DxSimpleItem data-field="status" editor-type="dxSelectBox" :label="{ text: 'Статус' }" :editor-options="{ items: statusItems }" />
            <DxSimpleItem data-field="landing_path" editor-type="dxTextBox" :label="{ text: 'Landing path' }" />
            <DxSimpleItem :label="{ text: 'Группа' }" template="groupField" />
            <DxSimpleItem :label="{ text: 'Edge-нода' }" template="nodeField" />
            <template #nameReadonly>
              <div class="mono">{{ form.name }}</div>
            </template>
            <template #groupField>
              <DxSelectBox
                v-model:value="form.group_id"
                :items="groups"
                value-expr="id"
                display-expr="name"
                width="100%"
              />
            </template>
            <template #nodeField>
              <DxSelectBox
                v-model:value="form.tracker_node_id"
                :items="nodeSelectItems"
                value-expr="id"
                :display-expr="(n: TrackerNode) => (n.public_ip ? `${n.name} (${n.public_ip})` : n.name)"
                width="100%"
              />
            </template>
          </DxForm>
        </template>
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="{ text: 'Отмена', onClick: closeModal }" />
        <DxToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          :options="{ text: saving ? 'Сохранение…' : 'Сохранить', type: 'default', disabled: saving, onClick: save }"
        />
      </DxPopup>
    </template>
  </TrackerListLayout>
</template>

<style scoped>
.form-error {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fdecea;
  color: #c62828;
  font-size: 0.875rem;
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}

.dns-badge {
  display: inline-block;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: #eceff1;
  color: #455a64;
}
.dns-badge--ok {
  background: #e8f5e9;
  color: #2e7d32;
}
.dns-badge--bad {
  background: #fdecea;
  color: #c62828;
}
.dns-badge--unk {
  background: #fff8e1;
  color: #f57f17;
}
</style>
